/* ============================================================
   <quote-form> — formulaire de soumission
   ------------------------------------------------------------
   - Type de client : affiche le champ "Entreprise" (requis) si coché.
   - Produits : autoprédiction avec images via /search/suggest.json
     (même source que la recherche du menu), multi-sélection en chips.
   - Logo : drag & drop -> POST {backend}/api/customizer/uploads
     (stocké dans Shopify Files) ; URL + id joints à la soumission.
   - L'envoi passe par le formulaire de contact Shopify.
   ============================================================ */

class QuoteForm extends HTMLElement {
  connectedCallback() {
    this.backendUrl = (this.dataset.backendUrl || '').replace(/\/+$/, '');
    this.selectedProducts = []; // { id, title, url, image }
    this.uploading = false;
    try {
      this.i18n = JSON.parse(this.querySelector('[data-qf-i18n]').textContent);
    } catch (e) {
      this.i18n = {};
    }

    this.bindClientType();
    this.bindSearch();
    this.bindUpload();
    this.bindSubmit();
  }

  // Traduction (clés injectées par la section) ; repli français.
  t(key, fallback) {
    return (this.i18n && this.i18n[key]) || fallback || key;
  }

  /* ---------------- Type de client ---------------- */
  bindClientType() {
    const companyField = this.querySelector('[data-qf-company]');
    const companyInput = companyField ? companyField.querySelector('input') : null;
    this.querySelectorAll('[data-qf-type]').forEach((radio) => {
      radio.addEventListener('change', () => {
        const isCompany = radio.value === 'Entreprise' && radio.checked;
        if (companyField) companyField.classList.toggle('hidden', !isCompany);
        if (companyInput) {
          companyInput.required = isCompany;
          if (isCompany) companyInput.focus();
        }
      });
    });
  }

  /* ---------------- Recherche produits (autoprédiction) ---------------- */
  bindSearch() {
    const input = this.querySelector('[data-qf-search-input]');
    const results = this.querySelector('[data-qf-results]');
    if (!input || !results) return;

    let timer = null;
    let lastQuery = '';

    input.addEventListener('input', () => {
      const q = input.value.trim();
      clearTimeout(timer);
      if (q.length < 2) {
        this.hideResults();
        return;
      }
      timer = setTimeout(() => {
        lastQuery = q;
        this.fetchSuggestions(q, lastQuery);
      }, 250);
    });

    // fermer au clic extérieur / Échap
    document.addEventListener('click', (e) => {
      if (!this.querySelector('[data-qf-search]').contains(e.target)) this.hideResults();
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.hideResults();
      if (e.key === 'Enter') e.preventDefault(); // ne pas soumettre le form depuis la recherche
    });
  }

  async fetchSuggestions(query, lastQuery) {
    const results = this.querySelector('[data-qf-results]');
    try {
      const url =
        '/search/suggest.json?q=' +
        encodeURIComponent(query) +
        '&resources[type]=product&resources[limit]=8';
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      const data = await res.json();
      // réponse obsolète (l'utilisateur a continué à taper) : ignorer
      const input = this.querySelector('[data-qf-search-input]');
      if (input && input.value.trim() !== lastQuery) return;

      const products = (((data || {}).resources || {}).results || {}).products || [];
      const visible = products.filter(
        (p) =>
          !/frais d'impression/i.test(p.title || '') &&
          !this.selectedProducts.some((s) => String(s.id) === String(p.id))
      );

      results.innerHTML = '';
      if (visible.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'qf__results-empty';
        empty.textContent = this.t('no_results', 'Aucun produit trouvé — décrivez-le dans le champ ci-dessous.');
        results.appendChild(empty);
      } else {
        visible.forEach((p) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'qf__result';
          btn.setAttribute('role', 'option');

          const img = document.createElement('img');
          img.src = p.image || (p.featured_image && p.featured_image.url) || '';
          img.alt = '';
          img.width = 44;
          img.height = 44;
          img.loading = 'lazy';
          btn.appendChild(img);

          const info = document.createElement('span');
          info.className = 'qf__result-info';
          const title = document.createElement('strong');
          title.textContent = p.title;
          info.appendChild(title);
          if (p.price) {
            const price = document.createElement('small');
            price.textContent = this.t('from_price', 'À partir de') + ' ' + p.price + ' $';
            info.appendChild(price);
          }
          btn.appendChild(info);

          btn.addEventListener('click', () => {
            this.addProduct({
              id: p.id,
              title: p.title,
              url: p.url,
              image: img.src,
            });
            const input = this.querySelector('[data-qf-search-input]');
            if (input) {
              input.value = '';
              input.focus();
            }
            this.hideResults();
          });
          results.appendChild(btn);
        });
      }
      results.classList.remove('hidden');
    } catch (e) {
      this.hideResults();
    }
  }

  hideResults() {
    const results = this.querySelector('[data-qf-results]');
    if (results) results.classList.add('hidden');
  }

  addProduct(product) {
    if (this.selectedProducts.some((p) => String(p.id) === String(product.id))) return;
    this.selectedProducts.push(product);
    this.renderChips();
  }

  removeProduct(id) {
    this.selectedProducts = this.selectedProducts.filter((p) => String(p.id) !== String(id));
    this.renderChips();
  }

  renderChips() {
    const wrap = this.querySelector('[data-qf-chips]');
    const hidden = this.querySelector('[data-qf-products]');
    if (!wrap) return;
    wrap.innerHTML = '';

    this.selectedProducts.forEach((p) => {
      const chip = document.createElement('span');
      chip.className = 'qf__chip-product';

      const img = document.createElement('img');
      img.src = p.image || '';
      img.alt = '';
      img.width = 32;
      img.height = 32;
      chip.appendChild(img);

      const label = document.createElement('span');
      label.textContent = p.title;
      chip.appendChild(label);

      const rm = document.createElement('button');
      rm.type = 'button';
      rm.setAttribute('aria-label', this.t('remove', 'Retirer') + ' ' + p.title);
      rm.innerHTML = '&times;';
      rm.addEventListener('click', () => this.removeProduct(p.id));
      chip.appendChild(rm);

      wrap.appendChild(chip);
    });

    if (hidden) {
      hidden.value = this.selectedProducts
        .map((p) => p.title + ' (' + (p.url || '') + ')')
        .join(' | ');
    }
  }

  /* ---------------- Logo (upload backend, comme l'éditeur) ---------------- */
  bindUpload() {
    const drop = this.querySelector('[data-qf-drop]');
    const file = this.querySelector('[data-qf-file]');
    const removeBtn = this.querySelector('[data-qf-remove]');

    if (file) {
      file.addEventListener('change', (e) => {
        const f = e.target.files && e.target.files[0];
        if (f) this.uploadLogo(f);
      });
    }
    if (drop) {
      ['dragenter', 'dragover'].forEach((evt) =>
        drop.addEventListener(evt, (e) => {
          e.preventDefault();
          drop.classList.add('is-dragover');
        })
      );
      ['dragleave', 'drop'].forEach((evt) =>
        drop.addEventListener(evt, (e) => {
          e.preventDefault();
          drop.classList.remove('is-dragover');
        })
      );
      drop.addEventListener('drop', (e) => {
        const f = e.dataTransfer.files && e.dataTransfer.files[0];
        if (f) this.uploadLogo(f);
      });
    }
    if (removeBtn) removeBtn.addEventListener('click', () => this.removeLogo());
  }

  setUploadStatus(message, type) {
    const el = this.querySelector('[data-qf-upload-status]');
    if (!el) return;
    el.className = 'qf__upload-status qf__upload-status--' + type;
    el.textContent = message;
  }

  async uploadLogo(file) {
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      this.setUploadStatus(this.t('err_format', 'Format non supporté. Utilisez PNG, JPEG ou WEBP.'), 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.setUploadStatus(this.t('err_size', 'Fichier trop volumineux (max 10 Mo).'), 'error');
      return;
    }

    // aperçu local immédiat
    const preview = this.querySelector('[data-qf-preview]');
    const previewImg = this.querySelector('[data-qf-preview-img]');
    const previewName = this.querySelector('[data-qf-preview-name]');
    const dropLabel = this.querySelector('[data-qf-drop-label]');
    const reader = new FileReader();
    reader.onload = (e) => {
      if (previewImg) previewImg.src = e.target.result;
      if (previewName) previewName.textContent = file.name;
      if (preview) preview.classList.remove('hidden');
      if (dropLabel) dropLabel.classList.add('hidden');
    };
    reader.readAsDataURL(file);

    if (!this.backendUrl) {
      this.setUploadStatus(this.t('upload_deferred', 'Logo joint (envoi différé — URL backend non configurée).'), 'error');
      return;
    }

    const idInput = this.querySelector('[data-qf-logo-id]');

    const fd = new FormData();
    fd.append('file', file);
    fd.append('placement', 'front');

    this.uploading = true;
    this.setUploadStatus(this.t('uploading', 'Envoi du logo…'), 'loading');
    try {
      const res = await fetch(this.backendUrl + '/api/customizer/uploads', {
        method: 'POST',
        body: fd,
      });
      let data = {};
      try {
        data = await res.json();
      } catch (e) {
        data = {};
      }
      if (!res.ok || data.ok === false) throw new Error(data.error || 'Erreur ' + res.status);

      const urlInput = this.querySelector('[data-qf-logo-url]');
      const cleanUrl = data.imageUrl ? String(data.imageUrl).replace(/[<>"'\\]/g, '').trim() : '';
      if (idInput) idInput.value = data.id || '';

      // Depuis le passage à Google Cloud Storage, imageUrl est toujours
      // renseignée quand ok:true — plus aucune attente n'est nécessaire.
      // Garde-fou : ne jamais laisser le champ vide dans le courriel.
      if (urlInput) {
        urlInput.value = cleanUrl || (data.id ? this.t('processing_ref', 'Lien indisponible — référence : ') + data.id : '');
      }
      this.setUploadStatus(this.t('uploaded', 'Logo enregistré ✓'), 'success');
    } catch (err) {
      this.setUploadStatus(this.t('upload_failed', 'Échec de l’envoi : ') + String((err && err.message) || err), 'error');
    }
    this.uploading = false;
  }

  removeLogo() {
    const idInput = this.querySelector('[data-qf-logo-id]');
    const urlInput = this.querySelector('[data-qf-logo-url]');
    if (idInput) idInput.value = '';
    if (urlInput) urlInput.value = '';

    const preview = this.querySelector('[data-qf-preview]');
    const dropLabel = this.querySelector('[data-qf-drop-label]');
    const file = this.querySelector('[data-qf-file]');
    if (preview) preview.classList.add('hidden');
    if (dropLabel) dropLabel.classList.remove('hidden');
    if (file) file.value = '';
    const status = this.querySelector('[data-qf-upload-status]');
    if (status) status.classList.add('hidden');
  }

  /* ---------------- Envoi ---------------- */
  bindSubmit() {
    const form = this.querySelector('form');
    if (!form) return;
    form.addEventListener('submit', (e) => {
      // attendre la fin de l'upload du logo
      if (this.uploading) {
        e.preventDefault();
        this.setUploadStatus(this.t('upload_wait', 'Envoi du logo en cours, un instant…'), 'loading');
      }
    });
  }
}

customElements.define('quote-form', QuoteForm);
